import { z } from 'zod';
import { ClassIdSchema } from './classId';

/**
 * **FI-17 (D7)** — Configurazione di una mossa multiclasse.
 *
 * Una mossa multiclasse permette al PG di "pescare" una mossa da un'altra
 * classe (manuale V1 rr. 1764-1773). Lo schema modella in modo additivo
 * tutti i pattern censiti nel manuale DWR (28 mosse description-only in
 * pre-D7):
 *  - il **pool** delle classi pescabili (ristretto a N classi specifiche
 *    via `'fixed-classes'`, oppure aperto «qualsiasi altra classe» via
 *    `'any-other-class'`);
 *  - eventuali **aggiustamenti di livello** del pool (es. *Combattente
 *    Multiclasse* «considera te di un livello inferiore»), con eccezioni
 *    opzionali per classi-source specifiche (es. *Dilettante Magico* non
 *    applica il delta alle mosse «basate sulla magia»; *Le Vie del Signore
 *    sono Infinite* non lo applica a Mago/Monaco);
 *  - eventuali **estensioni del pool** condizionate a un archetipo del PG
 *    (es. Sciamano-Elfo aggiunge Mago al pool di *Amico degli Spiriti*,
 *    Warlock-Elfo/Mezz'orco estendono il pool di *Le Vie del Patrono*);
 *  - eventuali **estensioni del pool** condizionate a una mossa già
 *    posseduta (es. Druido con *La Canzone delle Fate* aggiunge Bardo al
 *    pool di *Fratello del Cacciatore* e *Sorella del Predatore*);
 *  - eventuali **esclusioni** del pool basate su scelte multiclasse
 *    precedenti (vincolo storico di *Eclettico* e *Poliedrico* del Bardo:
 *    «diversa dalla classe scelta in *Versatile*»).
 *
 * **Filtro nativo deterministico** (hardcoded nel service): il pool delle
 * mosse-target esclude SEMPRE le mosse con `tags: ['multiclasse']`
 * (manuale: «Non prendere mosse multiclasse»). Non è opzionale dello
 * schema — non c'è caso del manuale in cui questo filtro vada disattivato.
 *
 * **Cardinalità**: una mossa ha al massimo 1 configurazione `multiclass`
 * (singolare). Il `MoveSchema.tags: ['multiclasse']` resta indipendente
 * e funge da marker testuale (la presenza di `multiclass` configurato
 * implica già che la mossa è multiclasse; il tag è ridondante ma utile
 * per filtri rapidi e retrocompat con i dati description-only).
 */

/**
 * Pool delle classi pescabili. Discriminated union su `kind`:
 *  - **`'fixed-classes'`**: classi specifiche dichiarate dalla mossa.
 *    Tipico delle mosse «schizzinose» del manuale (es. *Compagno d'Armi*
 *    del Guerriero pesca solo da Paladino/Ranger; *Amico degli Spiriti*
 *    dello Sciamano solo da Barbaro/Druido).
 *  - **`'any-other-class'`**: pool aperto a qualsiasi classe DIVERSA dalla
 *    propria. Tipico delle mosse «generiche» del manuale (es. *Combattente
 *    Multiclasse* del Guerriero, *Versatile* del Bardo, *Dilettante Magico*
 *    del Mago).
 *  - **`'from-extensions-only'`**: pool base **vuoto**, popolato solo dalle
 *    `extendsByArchetype` / `extendsByMove` della `MulticlassConfig`. Tipico
 *    delle mosse multiclasse il cui pool dipende **interamente** da una
 *    mossa-config posseduta dal PG (es. *La Via dell'Eremita* del Monaco:
 *    pool = solo le 2 classi della Regola scelta + eventuale estensione
 *    archetype). Refine cross-field: se kind = `'from-extensions-only'`,
 *    almeno una `extendsByArchetype` o `extendsByMove` deve essere presente
 *    nella `MulticlassConfig` (altrimenti il pool è sempre vuoto).
 */
export const MulticlassPicksSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('fixed-classes'),
    /**
     * Classi cui è ristretta la scelta. Min 1, max 11 (tutte tranne la
     * propria). Lo schema non verifica che la classe della mossa NON sia
     * in `classes` (vincolo applicativo da test data-side).
     */
    classes: z.array(ClassIdSchema).min(1).max(11),
  }),
  z.object({
    kind: z.literal('any-other-class'),
  }),
  z.object({
    kind: z.literal('from-extensions-only'),
  }),
]);
export type MulticlassPicks = z.infer<typeof MulticlassPicksSchema>;

/**
 * Aggiustamento di livello ai fini della scelta della mossa-target.
 *
 * Esempi del manuale:
 *  - *Combattente Multiclasse* (Guerriero L2-5): `delta: -1`, nessuna
 *    eccezione. A L2 il Guerriero pesca come Mago L1 → solo starting.
 *  - *Dilettante Magico* (Mago L2-5): `delta: -1`, eccezione «mosse basate
 *    sulla magia». Nel manuale l'eccezione è qualitativa (descrittiva, non
 *    formale); lasciamo il campo `moveTags` opzionale per estensioni
 *    future quando emergerà un tag formale come `'magia'`.
 *  - *Le Vie del Signore sono Infinite* (Chierico L6-10): `delta: -1`,
 *    eccezione su Mago/Monaco (le 2 classi sorgente del *Seminario
 *    Sperimentale* — vedi FI-37). Modellabile con `sourceClassIds: ['mago',
 *    'monaco']`.
 */
export const MulticlassLevelAdjustmentSchema = z.object({
  /**
   * Delta sul livello del PG (intero non positivo). Esempi:
   *  - `-1`: «considera te di un livello inferiore» (Combattente Multiclasse
   *    Guerriero, Dilettante Magico Mago, Le Vie del Signore sono Infinite
   *    Chierico)
   *  - `-5`: D10 / FI-11 — Adepto Multiclasse e Esperto Multiclasse
   *    (PG Instancabile L10 effettivo → livello target L5 → pool starting +
   *    advanced_2_5)
   *  - `-9`: D10 / FI-11 — Apprendista Multiclasse (PG Instancabile L10 →
   *    livello target L1 → solo starting)
   *  - `0`: ammesso per coerenza ma equivale a omettere l'oggetto intero.
   *
   * **Cambio schema D10 (additivo)**: union ristretto `-1 | 0` rimosso.
   * Tutti i valori non-positivi sono accettati. Retrocompat: i `delta: -1`
   * pre-D10 restano validi.
   */
  delta: z.number().int().lte(0),
  /**
   * Eccezioni in cui il delta NON si applica. Se il pool target lookup
   * matcha una delle eccezioni, il delta viene azzerato per quella mossa.
   */
  exceptions: z
    .object({
      /**
       * Classi-source che bypassano il delta (es. `['mago', 'monaco']`
       * per *Le Vie del Signore sono Infinite*).
       */
      sourceClassIds: z.array(ClassIdSchema).optional(),
      /**
       * Tag della mossa-target che bypassano il delta (es. `['magia']`
       * per *Dilettante Magico* — non ancora popolato come tag formale
       * nel catalogo, ma lasciato per estensibilità).
       */
      moveTags: z.array(z.string().min(1)).optional(),
    })
    .optional(),
});
export type MulticlassLevelAdjustment = z.infer<typeof MulticlassLevelAdjustmentSchema>;

/**
 * Estensione del pool classi condizionata a un archetipo del PG.
 *
 * Esempi del manuale:
 *  - *Amico degli Spiriti* (Sciamano L2-5): `archetypeId: 'elfo'`,
 *    `extraClasses: ['mago']` — lo Sciamano-Elfo può pescare anche dal
 *    Mago oltre a Barbaro/Druido base.
 *  - *Le Vie del Patrono* (Warlock L2-5): 2 estensioni per Elfo (Mago)
 *    e Mezz'orco (Guerriero) — array di 2 entry.
 *
 * **Lookup**: applicato sia su `Character.archetypeId` (primario) sia su
 * `Character.secondaryArchetypeId` (FI-1 esteso da *Infanzia Complessa*).
 */
export const MulticlassExtendsByArchetypeSchema = z.object({
  /** ID dell'archetipo del PG che attiva l'estensione (es. `'elfo'`). */
  archetypeId: z.string().min(1),
  /** Classi extra aggiunte al pool quando il PG ha quell'archetipo. */
  extraClasses: z.array(ClassIdSchema).min(1),
});
export type MulticlassExtendsByArchetype = z.infer<typeof MulticlassExtendsByArchetypeSchema>;

/**
 * Estensione del pool classi condizionata a una mossa già posseduta dal PG.
 *
 * Esempio del manuale:
 *  - *Fratello del Cacciatore* / *Sorella del Predatore* (Druido): «pool =
 *    Chierico/Ranger, +Bardo se hai *La Canzone delle Fate*». Modellato
 *    come `moveId: 'druido.la-canzone-delle-fate'`, `extraClasses: ['bardo']`.
 *
 * **Lookup**: il PG ha la mossa se `Character.moves.some(m => m.id === moveId)`.
 */
export const MulticlassExtendsByMoveSchema = z.object({
  /** ID della mossa che, se posseduta dal PG, attiva l'estensione. */
  moveId: z.string().min(1),
  /** Classi extra aggiunte al pool quando il PG possiede quella mossa. */
  extraClasses: z.array(ClassIdSchema).min(1),
});
export type MulticlassExtendsByMove = z.infer<typeof MulticlassExtendsByMoveSchema>;

/**
 * **FI-44 (2026-05-15)** — Estensione del pool classi condizionata
 * **all'opzione scelta** in `selectedChoices` di una mossa-config già
 * posseduta dal PG. Più granulare di `extendsByMove` (che guarda solo
 * presenza/assenza della mossa).
 *
 * Esempio del manuale:
 *  - *Seconda Laurea* (Chierico L6-10, manuale r. 2930): pool dipende dal
 *    «tipo di *Seminario Sperimentale* che hai frequentato». Mapping:
 *    `regola-battaglia/lavoro/pieta` → Monaco; `scuola-giovani-maghi/scudo-magico`
 *    → Mago.
 *
 * **Lookup**: per ogni `choiceId` in `Character.moves[moveId].selectedChoices`,
 * `classesByChoice[choiceId]` (se definita) viene unita al pool. Choices
 * non in `classesByChoice` sono ignorate. Se la mossa non è posseduta,
 * niente effetto.
 */
export const MulticlassExtendsByMoveChoiceSchema = z.object({
  /** ID della mossa-config (con `selectedChoices`) di cui leggere la scelta. */
  moveId: z.string().min(1),
  /**
   * Mappa `choiceId → classi extra` aggiunte al pool quando il PG ha
   * quella choice in `selectedChoices` della mossa-source.
   */
  classesByChoice: z.record(z.string().min(1), z.array(ClassIdSchema).min(1)),
});
export type MulticlassExtendsByMoveChoice = z.infer<typeof MulticlassExtendsByMoveChoiceSchema>;

/**
 * Configurazione completa multiclasse di una `MoveSchema`. Tutti i campi
 * sotto `picks` sono opzionali — l'unico obbligatorio è il pool di base.
 *
 * **Lookup di esclusione**: il vincolo storico Bardo *Eclettico* /
 * *Poliedrico* («classe diversa da quella scelta in *Versatile*») è modellato come
 * `excludesClassesPickedInMoves: string[]` — array di ID di mosse multiclasse
 * precedenti. Per ogni `levelUpHistory[i].multiclassPick` con `moveAdded` ∈
 * `excludesClassesPickedInMoves`, la `sourceClassId` viene esclusa dal pool.
 */
export const MulticlassConfigSchema = z
  .object({
    picks: MulticlassPicksSchema,
    levelAdjustment: MulticlassLevelAdjustmentSchema.optional(),
    extendsByArchetype: z.array(MulticlassExtendsByArchetypeSchema).min(1).optional(),
    extendsByMove: z.array(MulticlassExtendsByMoveSchema).min(1).optional(),
    /** **FI-44 (2026-05-15)**: extends più granulare su `selectedChoices` di altra mossa. */
    extendsByMoveChoice: z.array(MulticlassExtendsByMoveChoiceSchema).min(1).optional(),
    excludesClassesPickedInMoves: z.array(z.string().min(1)).min(1).optional(),
  })
  .refine(
    (cfg) => {
      if (cfg.picks.kind !== 'from-extensions-only') return true;
      const hasArchetypeExt = (cfg.extendsByArchetype?.length ?? 0) > 0;
      const hasMoveExt = (cfg.extendsByMove?.length ?? 0) > 0;
      const hasMoveChoiceExt = (cfg.extendsByMoveChoice?.length ?? 0) > 0;
      return hasArchetypeExt || hasMoveExt || hasMoveChoiceExt;
    },
    {
      message:
        "MulticlassConfig.picks.kind 'from-extensions-only' richiede almeno una entry in extendsByArchetype, extendsByMove o extendsByMoveChoice (altrimenti il pool è sempre vuoto).",
      path: ['picks'],
    },
  );
export type MulticlassConfig = z.infer<typeof MulticlassConfigSchema>;
